import React, { useEffect, useRef, useState } from "react";
import LockerDetailsComponent from "../components/LockerDetailsComponent";
import DateTimeSelector from "../components/DateTimeSelector";
import { useHistory } from "react-router-dom";
import { advanceBookingLockerBankDetails, getTenantAndAssignmentSettings } from "../api/api";
import { getSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from "../lib/BrowserStorageAccessMiddleware";
import { ADVANCE_BOOKING, LOCAL_STORAGE, LOCKER_BANK_ADDRESS, LOCKER_SLUG, LS_BOOKING_TYPE, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS } from "../assets/constants/BrowserStorageKeys";
import { useTranslation } from "react-i18next";
import { applyBackendLanguageConfig, buildAddress, buildLockerDetailsData, checkIfAssignmentIsProperlyConfigured, getNoFreeLockerErrorText, getPathForNextPage, getPriceFor24Hr, getPriceForSelectedTiming, getRoundedDurationInHours, getSlotIdFor24Hourse, getUnitLabelParams, getUnitLabelParamsFromBool, redirectToLockerDetails, setDomainAndLockerBankIdInLocalStorage } from "../lib/Utils";
import { LOCKER_DETAILS, LOCKER_SIZE_SELECTION, BOOKING_SUMMARY } from "../assets/constants/PageList";
import ShowToast from "../components/ToastComponent";
import { TOAST_ERROR, POD_BANK_TYPE, SUCCESS } from "../assets/constants/Constants";
import CircularProgressLoader from "../components/CircularProgressLoader";

export default function LockerDetailsPage() {
  const history = useHistory();
  const { t } = useTranslation();
  const { lockerDetails } = history.location?.state || {};
  const [showDateTimeSelector, setShowDateTimeSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(() => {
    if (lockerDetails) return false;
    const searchParams = new URLSearchParams(history.location.search);
    return Boolean(searchParams.get('lockerBankId'));
  });
  const [loadingMessage, setLoadingMessage] = useState('');
  const initialRender = useRef(true);

  const validateLockerBankAndRedirect = async (lockerBankId, domain) => {
    try {
      const response = await getTenantAndAssignmentSettings(lockerBankId);
      if (!response || response.status !== SUCCESS) {
        setIsLoading(false);
        console.error("Invalid response from API");
        return;
      }

      const tempResponse = response?.data?.locker_bank;
      const lockerBankAddress = buildAddress(tempResponse);
      storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_BANK_ADDRESS, lockerBankAddress);

      applyBackendLanguageConfig(response);
      const assignments = response?.data?.locker_assignments || [];
      const lokcerCurrency = assignments[0]?.currency || '';

      if (assignments.length > 1) {
        setIsLoading(false);
        history.replace({
          pathname: "/assignments",
          state: {
            tenantAndAssignmentSettings: response.data
          }
        });
        return;
      }

      if (assignments.length === 1) {
        const isAssignmentProperlyConfigured = await checkIfAssignmentIsProperlyConfigured(response.data);
        if (isAssignmentProperlyConfigured === true) {
          storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_SLUG, tempResponse?.slug);
          const templockerData = buildLockerDetailsData(tempResponse, lockerBankAddress, domain);
          setIsLoading(false);
          await redirectToLockerDetails(history, 'replace', templockerData, lokcerCurrency, history.location.pathname);
        } else {
          setIsLoading(false);
          console.error("Assignment not properly configured:", isAssignmentProperlyConfigured);
        }
        return;
      }

      setIsLoading(false);
      console.error("No locker assignments found");
    } catch (error) {
      setIsLoading(false);
      console.error("Error while getting settings and validation:", error);
    }
  };

  useEffect(() => {
    if (!initialRender.current) return;
    initialRender.current = false;

    if (lockerDetails) return;

    const searchParams = new URLSearchParams(history.location.search);
    const lockerBankId = searchParams.get('lockerBankId');
    const domain = searchParams.get('domain');

    if (lockerBankId) {
      setDomainAndLockerBankIdInLocalStorage({ domain, lockerBankId });
      setIsLoading(true);
      setLoadingMessage(t('pleaseWaitText'));
      validateLockerBankAndRedirect(lockerBankId, domain);
    } else {
      console.error("locker bank id is not there please contact to admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleBookStore = () => {
    setShowDateTimeSelector(true);

    // Scroll to DateTimeSelector smoothly
    setTimeout(() => {
      document
        .querySelector(".locker-details-page__datetime-section")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 100);
  };

  const handleDateTimeContinue = async(dateTimeData) => {
    // Combine locker details + datetime
    const bookingData = {
        locker: lockerDetails,
        ...dateTimeData,
    };
  const requestData = {
    lockerBankId: lockerDetails?.id,
    reserve_start: bookingData?.reserve_start,
    reserve_end: bookingData?.reserve_end
  };

    let response;
    try {
      response = await advanceBookingLockerBankDetails(requestData);
    } catch (error) {
      console.error("advanceBookingLockerBankDetails error:", error);
      const message = t('error_msg_something_went_wrong_try_again');
      ShowToast(t(message), TOAST_ERROR);
      return false;
    }

    if (!response?.data?.data) {
      ShowToast(t("error_msg_something_went_wrong_try_again"), TOAST_ERROR);
      return false;
    }

    const advancelockerResponse = response?.data?.data;

    const tempdata = {
        lockerBankId: advancelockerResponse?.id,
        bookingTimingObject: {
          ...dateTimeData
        },
        assignmentSlug: advancelockerResponse?.locker_assignments[0]?.slug,
        locker_bank_slug:advancelockerResponse?.slug,
        userId: null,
        availableLockerSizes: advancelockerResponse?.door_sizes || [],
        isManualLockerSelectionEnabled: advancelockerResponse?.isManualLockerSelectionEnabled,
        IsAdvanceBooking: true,
        lockerCurrency: advancelockerResponse?.locker_assignments[0]?.currency,
    };
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_BOOKING_TYPE, ADVANCE_BOOKING )

    const isPodLockerType = String(lockerDetails?.LockerBankType).toLowerCase() === POD_BANK_TYPE;

    if (isPodLockerType) {
      const hasFreeLocker = tempdata.availableLockerSizes?.some(
        (lockerSize) => Number(lockerSize?.available_doors_count) > 0
      );

      if (! hasFreeLocker) {
        ShowToast(getNoFreeLockerErrorText(isPodLockerType, t), TOAST_ERROR);
        return false;
      }

      const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
      const isPricingEnabled = tenantAndAssignmentSettings?.is_pricing;
      const podLockerSize = tempdata.availableLockerSizes?.[0];
      const price = isPricingEnabled
        ? getPriceForSelectedTiming(
            getRoundedDurationInHours(dateTimeData?.reserve_start, dateTimeData?.reserve_end),
            getPriceFor24Hr(podLockerSize?.size_pricing)
          )
        : 0;

      const { Unit } = getUnitLabelParamsFromBool(isPodLockerType, t);

      const selectedLockers = podLockerSize
        ? [
            {
              door_size: podLockerSize?.code,
              price,
              slot_id: getSlotIdFor24Hourse(podLockerSize?.size_pricing),
              name: `${podLockerSize?.name} ${Unit}`,
            },
          ]
        : [];

      history.push({
        pathname: getPathForNextPage(LOCKER_DETAILS, BOOKING_SUMMARY),
        state: {
          ...tempdata,
          lockerName: lockerDetails?.name,
          selectedLockers,
        },
      });

      return true;
    }

    history.push({
        pathname: getPathForNextPage(LOCKER_DETAILS, LOCKER_SIZE_SELECTION),
        state: { ...tempdata }
    });

    return true;
  };

  if (!lockerDetails) {
    if (isLoading) {
      return (
        <div className="locker-details-page">
          <CircularProgressLoader message={loadingMessage} />
        </div>
      );
    }
  }

  return (
    <div className="locker-details-page">
        {!showDateTimeSelector ? (
          <div className="locker-details-container">
            <LockerDetailsComponent
              locker={lockerDetails}
              onBook={handleBookStore}
            />
          </div>
        ) : (
          <div className="locker-datetime-picker-container">
            <DateTimeSelector onContinue={handleDateTimeContinue} lockerType={lockerDetails?.LockerBankType} />
          </div>
        )}
      </div>
  );
}
